import {
  applyOrderToMenu,
  clearSidebarOrderMap,
  loadSidebarOrderMap,
  saveSidebarOrderMap,
  getSidebarMenu,
  type MenuGroup,
  type SidebarOrderMap,
} from 'modules/admin/components/Navbars/sidebarConfig';
import { useCallback, useState } from 'react';

/**
 * useSidebarOrder
 *
 * - On mount: reads localStorage and applies saved order to sidebarMenu
 * - reorderItem: move an item within a group (call during drag-drop)
 * - persistOrder: writes current order to localStorage
 * - resetOrder: clears localStorage, restores default sidebarMenu
 */
export const useSidebarOrder = () => {
  const sidebarMenu = getSidebarMenu();
  const [orderedMenu, setOrderedMenu] = useState<MenuGroup[]>(() => {
    const saved = loadSidebarOrderMap();
    return Object.keys(saved).length ? applyOrderToMenu(sidebarMenu, saved) : sidebarMenu;
  });

  const [isCustomized, setIsCustomized] = useState<boolean>(
    () => !!Object.keys(loadSidebarOrderMap()).length
  );

  /** Move item at fromIndex to toIndex within groupTitle */
  const reorderItem = useCallback((groupTitle: string, fromIndex: number, toIndex: number) => {
    setOrderedMenu((prev) =>
      prev.map((group) => {
        if (group.title !== groupTitle) return group;
        const items = [...group.items];
        const [moved] = items.splice(fromIndex, 1);
        items.splice(toIndex, 0, moved);
        return {
          ...group,
          items: items.map((item, idx) => ({ ...item, order: idx })),
        };
      })
    );
  }, []);

  /** Persist current orderedMenu to localStorage */
  const persistOrder = useCallback(
    (menuToSave?: MenuGroup[]) => {
      const source = menuToSave ?? orderedMenu;
      const map: SidebarOrderMap = {};
      source.forEach((group) => {
        map[group.title] = group.items.map((i) => i.key);
      });
      saveSidebarOrderMap(map);
      setIsCustomized(true);
    },
    [orderedMenu]
  );

  /** Clear localStorage and reset to default sidebarMenu */
  const resetOrder = useCallback(() => {
    clearSidebarOrderMap();
    setOrderedMenu(sidebarMenu);
    setIsCustomized(false);
  }, [sidebarMenu]);

  return { orderedMenu, setOrderedMenu, reorderItem, persistOrder, resetOrder, isCustomized };
};
